/**
 * useHaptic - Hook para feedback háptico estilo iOS
 * 
 * Usa navigator.vibrate() para simular taptic engine em dispositivos
 * que suportam vibração. Silenciosamente ignora em dispositivos sem suporte.
 */
import { useCallback } from 'react';

type HapticStyle = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

const hapticPatterns: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
  selection: 10,
  success: [10, 30, 10],
  warning: [20, 30, 20],
  error: [30, 50, 30, 50, 30],
};

export function useHaptic() {
  const trigger = useCallback((style: HapticStyle = 'light') => {
    // Verificar suporte
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) {
      return;
    }
    
    const pattern = hapticPatterns[style];
    
    try {
      navigator.vibrate(pattern);
    } catch {
      // Silenciosamente ignora erros
    }
  }, []);
  
  const impactLight = useCallback(() => trigger('light'), [trigger]);
  const impactMedium = useCallback(() => trigger('medium'), [trigger]);
  const impactHeavy = useCallback(() => trigger('heavy'), [trigger]);
  const selectionChanged = useCallback(() => trigger('selection'), [trigger]);
  const notificationSuccess = useCallback(() => trigger('success'), [trigger]);
  const notificationWarning = useCallback(() => trigger('warning'), [trigger]);
  const notificationError = useCallback(() => trigger('error'), [trigger]);
  
  return {
    trigger,
    impactLight,
    impactMedium,
    impactHeavy,
    selectionChanged,
    notificationSuccess,
    notificationWarning,
    notificationError,
  };
}

export type { HapticStyle };
